import React, { useContext, useEffect, useRef, useState } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { loginFormIcon } from '../../assets/images';
import { LoginFormContext } from '../../context/LoginFormContext';
import { LoginService, GetCountriesService } from '../../apis/auth';
import { tostE } from '../../config/toastConfig';
import { Button, Input, Select } from 'antd';
import { MailOutlined, MobileOutlined } from '@ant-design/icons';

export default function Welcome() {
    const { setCurrentStep, formData, setFormData } = useContext(LoginFormContext);
    const [loginMode, setLoginMode] = useState('mobile');
    const [countries, setCountries] = useState([]);
    const [selectedCode, setSelectedCode] = useState('+91');
    const codeFlagMap = useRef({});

    useEffect(() => {
        GetCountriesService()
            .then(res => {
                const data = res.data?.data;
                if (Array.isArray(data)) {
                    const seen = new Set();
                    const mapped = [];
                    const flagMap = {};
                    for (const item of data) {
                        const flag    = item?.flag || '';
                        const codeRaw = item?.dial_code;
                        const code    = String(codeRaw).startsWith('+') ? String(codeRaw) : `+${String(codeRaw)}`;
                        if (!seen.has(code)) {
                            seen.add(code);
                            flagMap[code] = flag;
                            mapped.push({
                                label: `${flag ? flag + ' ' : ''}${code} ${item.name}`.trim(),
                                value: code,
                            });
                        }
                    }
                    codeFlagMap.current = flagMap;
                    setCountries(mapped);
                }
            })
            .catch(() => {});
    }, []);

    const mobileSchema = Yup.object({
        mobile: Yup.string().required('Mobile number is required').min(7, 'Enter a valid mobile number'),
    });

    const emailSchema = Yup.object({
        email: Yup.string().required('Email is required').email('Enter a valid email'),
    });

    const formik = useFormik({
        initialValues: { mobile: '', email: '' },
        validationSchema: loginMode === 'email' ? emailSchema : mobileSchema,
        onSubmit: async (values) => {
            try {
                const payload = loginMode === 'email'
                    ? { email: values.email }
                    : { mobile: values.mobile, country_code: selectedCode };

                const res = await LoginService(payload);
                if (res.status === 200 && res.data.success) {
                    setFormData({
                        ...formData,
                        mobile: values.mobile,
                        country_code: selectedCode,
                        email: values.email,
                        login_mode: loginMode,
                    });
                    setCurrentStep(2);
                } else {
                    tostE(res.data.message);
                }
            } catch (error) {
                console.log(error);
            }
        }
    });

    const { errors, touched, getFieldProps, handleSubmit, isSubmitting, resetForm, setFieldValue } = formik;

    const toggleMode = () => {
        setLoginMode(prev => prev === 'mobile' ? 'email' : 'mobile');
        resetForm();
    };

    return (
        <>
            <span className='login-form-icon'>
                <img src={loginFormIcon} alt="loginFormIcon" />
            </span>
            <h4>Welcome</h4>
            <p>Please sign in or sign up below.</p>
            <FormikProvider value={formik}>
                <Form autoComplete='off' onSubmit={handleSubmit}>
                    <div className='form-group'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                            <label className='mb-0 fw-600 mt-2'>
                                {loginMode === 'email' ? 'Email' : 'Whatsapp Number'}
                            </label>
                            <button
                                type='button'
                                className='btn btn-link p-0'
                                style={{ fontSize: 13, color: '#595C5C', textDecoration: 'none' }}
                                onClick={toggleMode}
                            >
                                {loginMode === 'email'
                                    ? <><MobileOutlined className='me-1' />Use Whatsapp Number</>
                                    : <><MailOutlined className='me-1' />Use Email</>
                                }
                            </button>
                        </div>

                        {loginMode === 'email' ? (
                            <>
                                <Input
                                    type='email'
                                    size='large'
                                    {...getFieldProps('email')}
                                    onChange={e => setFieldValue('email', e.target.value)}
                                    status={touched.email && errors.email ? 'error' : ''}
                                    placeholder='Enter your email'
                                />
                                {touched.email && errors.email && (
                                    <div className='text-danger mt-1' style={{ fontSize: 12 }}>{errors.email}</div>
                                )}
                            </>
                        ) : (
                            <>
                                <Input.Group compact>
                                    <Select
                                        showSearch
                                        value={selectedCode}
                                        onChange={val => setSelectedCode(val)}
                                        options={countries}
                                        size='large'
                                        style={{ width: 105 }}
                                        filterOption={(input, opt) =>
                                            (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        popupMatchSelectWidth={260}
                                        labelRender={({ value }) => {
                                            const flag = codeFlagMap.current[value] || '';
                                            return `${flag ? flag + ' ' : ''}${value}`.trim();
                                        }}
                                    />
                                    <Input
                                        type='number'
                                        size='large'
                                        style={{ width: 'calc(100% - 105px)' }}
                                        {...getFieldProps('mobile')}
                                        onChange={e => setFieldValue('mobile', e.target.value)}
                                        status={touched.mobile && errors.mobile ? 'error' : ''}
                                        placeholder='Mobile number'
                                    />
                                </Input.Group>
                                {touched.mobile && errors.mobile && (
                                    <div className='text-danger mt-1' style={{ fontSize: 12 }}>{errors.mobile}</div>
                                )}
                            </>
                        )}
                    </div>
                    <div className='form-action'>
                        <Button htmlType='submit' type='primary' block className='mt-3' loading={isSubmitting} size='large'>
                            {loginMode === 'email' ? 'Continue with Email' : 'Continue with Phone'}
                        </Button>
                    </div>
                </Form>
            </FormikProvider>
        </>
    );
}
